import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Event, NavigationEnd, NavigationError, Router } from '@angular/router';
import { ScreenSizeDetectorService } from '@serviceplan/ui-kit';
import {
  AppSettings,
  AppVersionService,
  BackgroundProcess,
  BackgroundProcessService,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  InformationBannerService,
  StorageUtilityLib,
  USER_SESSION_TOKEN,
  UserData,
  UserSession,
  UserSessionService,
} from '@thehubz/client/core';
import { UserInfo } from '@thehubz/client/data-access-api';
import { brandedLogoUrl } from '@thehubz/shared/branding';
import { Observable, Subscription } from 'rxjs';

const mobileMaxScreenSize = 999;

@Component({
  selector: 'thehubz-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() redirectUri!: string;
  @Input() brandPortalLink?: string;
  @Input() collectionGuideLink?: string;

  @Output() loginRequest = new EventEmitter<boolean>();

  headline?: string;
  subHeadline?: { id: string; name: string };
  headerLink?: string;
  loggedInUser?: UserInfo;
  impersonated?: UserInfo;
  isGuidedTourEnabled?: boolean;

  private activeRoute = '';
  routingObject: any = {};
  historyObject: any;

  private headerTextSub?: Subscription;
  backgroundProcess$?: Observable<BackgroundProcess[]>;

  appUpdate = false;
  private appUpdateSub?: Subscription;

  private loginStateSubscription?: Subscription;

  logoUrl!: string;

  private mobileMq = window.matchMedia(`(max-width: ${mobileMaxScreenSize}px)`);
  isMobileNavVisible = false;
  isMobile = window.innerWidth <= mobileMaxScreenSize;

  constructor(
    @Inject(ENVIRONMENT) private env: ClientEnvironment,
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private storageUtility: StorageUtilityLib,
    private router: Router,
    private commonUtilityLib: CommonUtilityLib,
    private userSessionService: UserSessionService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private appVersionService: AppVersionService,
    private backgroundProcessService: BackgroundProcessService,
    private infoBannerService: InformationBannerService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {}

  ngOnInit() {
    this.loginStateSubscription = this.userSessionService.getLoggedInState().subscribe((loggedIn) => {
      if (loggedIn) {
        this.infoBannerService.run();
      }
    });

    this.isGuidedTourEnabled = this.env.isGuidedTourEnabled;

    const asUserInfo = (userData: UserData) => ({
      id: userData._id,
      name: userData.firstName + ' ' + userData.lastName,
      email: userData.email,
    });

    const userSession = this.storageUtility.getLocalStorageNullable<UserSession>(this.userSessionToken);
    if (userSession) {
      this.loggedInUser = asUserInfo(userSession.userData);
      if (userSession.userData.impersonate) {
        this.impersonated = asUserInfo(userSession.userData.impersonate);
      }
    }

    this.activeRoute = this.router.url;
    this.routingObject = this.commonUtilityLib.routingObjectGetter();
    if (this.activeRoute == '/assets') {
      this.routingObject.text = $localize`Asset overview`;
    }

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.activeRoute = event.url;
        this.routingObject = { ...this.commonUtilityLib.routingObjectGetter() };
        if (
          this.activeRoute === '/dashboard' ||
          this.activeRoute === '/projects' ||
          this.activeRoute === '/tasks' ||
          this.activeRoute === '/planning' ||
          this.activeRoute === '/assets' ||
          this.activeRoute === '/admin/dashboard' ||
          this.activeRoute === '/assets-basket' ||
          this.activeRoute === '/admin' ||
          this.activeRoute === '/admin/companies' ||
          this.activeRoute === '/admin/users' ||
          this.activeRoute === '/admin/groups' ||
          this.activeRoute === '/admin/workflows' ||
          this.activeRoute === '/admin/teams'
        ) {
          this.headline = undefined;
          this.subHeadline = undefined;
          this.headerLink = undefined;
          if (this.activeRoute === '/assets') {
            this.routingObject.text = $localize`Asset overview`;
          } else if (this.activeRoute === '/admin') {
            this.routingObject.text = $localize`Admin area`;
          }
        }
      }

      if (event instanceof NavigationError) {
        this.clientNotificationHandlerService.errorNotification(event.error);
      }
    });

    this.headerTextSub = this.commonUtilityLib.headerText$.subscribe((res) => {
      if (res) {
        this.headline = res.header;
        this.subHeadline = res.subHeader;
        this.headerLink = res.routeLinkPath;
      } else {
        this.headline = undefined;
        this.subHeadline = undefined;
        this.headerLink = undefined;
      }
    });

    this.logoUrl = brandedLogoUrl(this.env.brandingKey);

    this.appUpdateSub = this.appVersionService.versionChanged$.subscribe((value) => {
      this.appUpdate = value;
      if (value === true) {
        this.appUpdateSub?.unsubscribe();
      }
    });

    this.backgroundProcess$ = this.backgroundProcessService.backgroundProcess$;

    this.mobileMq.addEventListener('change', (event) => {
      this.isMobile = event.matches;
    });
  }

  logoutUser(impersonated?: boolean) {
    this.userSessionService.logoutUser(impersonated);
  }

  navUrl(url: string[]) {
    this.commonUtilityLib.routingObjectSetter(this.historyObject);
    this.router.navigate(url);
  }

  navigateToProfile() {
    this.historyObject = this.commonUtilityLib.routingObjectGetter();
    this.commonUtilityLib.routingObjectSetter(AppSettings.APP_CONFIG['navigation'][0].items[1]);
    this.router.navigate(['/user/profile']);
  }

  ngOnDestroy(): void {
    this.headerTextSub?.unsubscribe();
    this.appUpdateSub?.unsubscribe();
    this.loginStateSubscription?.unsubscribe();
  }

  onShowMainNav(): void {
    this.isMobileNavVisible = true;
  }

  onHideMainNav(): void {
    this.isMobileNavVisible = false;
  }
}
